import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useGetFieldAdminQEOversightQuery } from "./fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FAPageState } from "./FAStates";
import { FONT_FAMILY, WARN_YELLOW, OK_GREEN_TEXT, initialsOf, formatTimeOfDay } from "./designTokens";
import { FAStatTile, FAEmptyState } from "./FALayout";

import { resolveMediaUrl } from "./mediaUrl";
import { FAPageShell, FAPageHeader, FAUnlinkedNote } from "./FALayout";
import { PromptModal } from "./QEPromptDialog";

function waitingSince(iso) {
  const diffMin = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  const hr = Math.floor(diffMin / 60);
  const min = diffMin % 60;
  if (hr === 0) return `${min}m`;
  return `${hr}h ${min}m`;
}

function shortRef(referenceId) {
  return referenceId ? referenceId.slice(0, 8) : "—";
}

// Same mapping the Team page uses for this same person. Note this is the FA's
// attendance verification, which is a different thing from the design's "Active"
// (a QE-account state) — attendance is the only real per-person status we have.
function attendanceMeta(attendance) {
  if (!attendance) return { label: "No record", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" };
  if (attendance.conduct_flag) {
    return { label: attendance.severity ? `Flagged · ${attendance.severity}` : "Flagged", bg: "rgba(251,191,36,0.12)", color: WARN_YELLOW };
  }
  if (attendance.fa_verified) return { label: "Verified", bg: "rgba(34,197,94,0.12)", color: OK_GREEN_TEXT };
  return { label: "Not verified", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" };
}

export default function QEOversight() {
  const navigate = useNavigate();
  const { data, isSuccess, isError, error, refetch } = useGetFieldAdminQEOversightQuery();
  const [promptTarget, setPromptTarget] = useState(null);

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={4} tiles={2} />;

  const qe = data.qe;
  const oldestAwaiting = data.awaiting.length ? data.awaiting[0] : null;
  const progressPct = data.installs_done_today
    ? Math.min(100, Math.round((data.cocs_issued_today / data.installs_done_today) * 100))
    : 0;
  const allIssued = data.awaiting_count === 0;

  return (
    <>
      <FAPageShell>
        <FAPageHeader title="QE Oversight" sub={<>{qe ? (
              <>Read-only view of <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{qe.name}</Box>'s COC progress · sourced from the QE app &amp; IMS</>
            ) : (
              <>Read-only view of today's COC progress · sourced from the QE app &amp; IMS</>
            )}</>} sx={{ mb: "22px" }} />

        <FAUnlinkedNote count={data.unlinked_installers} />

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", backgroundColor: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.18)", borderRadius: "8px", p: "10px 14px", mb: "18px" }}>
          <LockOutlinedIcon sx={{ color: "#C4B5FD", fontSize: "14px", flexShrink: 0 }} />
          <Typography sx={{ color: "rgba(196,181,253,0.95)", fontSize: "0.78rem", lineHeight: 1.4 }}>
            Read-only · only the QE can issue or amend a COC. Use <Box component="span" sx={{ fontWeight: 700 }}>Prompt QE</Box> to send a nudge.
          </Typography>
        </Box>

        {/* QE hero — the person this whole page is about. Only renders once a QE is
            flagged for the team (Team page → "Assign Qualified Electrician"). */}
        {qe ? (
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "14px", p: "20px 22px", mb: "18px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px", mb: "18px", flexWrap: "wrap" }}>
              <Box sx={{ width: "52px", height: "52px", borderRadius: "50%", backgroundColor: "rgba(168,85,247,0.18)", color: "#C4B5FD", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.05rem", fontWeight: 700, flexShrink: 0, letterSpacing: "-0.02em" }}>
                {initialsOf(qe.name)}
              </Box>
              <Box sx={{ flex: 1, minWidth: "160px" }}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.015em" }}>{qe.name}</Typography>
                {/* The design's "Reg. ECB/QE-0418" has no backing field anywhere —
                    the real contact email takes that slot instead. */}
                <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>
                  Qualified Electrician{qe.email_address ? ` · ${qe.email_address}` : ""}
                </Typography>
              </Box>
              <AttendancePill attendance={qe.attendance} />
            </Box>

            <Box sx={{ mb: "16px" }}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px", mb: "8px" }}>
                <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>Today's COCs issued</Typography>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.015em" }}>
                  {data.cocs_issued_today}
                  <Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", fontWeight: 500 }}> / {data.installs_done_today} completed installs</Box>
                </Typography>
              </Box>
              <Box sx={{ height: "8px", backgroundColor: "rgba(255,255,255,0.06)", borderRadius: "999px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${progressPct}%`, background: "linear-gradient(90deg, #FDB940, #E0A020)", borderRadius: "999px" }} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", mt: "8px", fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", flexWrap: "wrap" }}>
                <Typography component="span" sx={{ fontSize: "0.74rem", fontWeight: 600, color: allIssued ? OK_GREEN_TEXT : WARN_YELLOW }}>
                  ● {allIssued ? "All caught up" : `${data.awaiting_count} awaiting`}
                </Typography>
                <Typography component="span" sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)" }}>
                  {data.last_issued_at ? `Last issued ${formatTimeOfDay(data.last_issued_at)}` : "Nothing issued yet today"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", pt: "14px", borderTop: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" }}>
              <Box
                component="button"
                type="button"
                disabled={!oldestAwaiting}
                onClick={oldestAwaiting ? () => setPromptTarget(oldestAwaiting) : undefined}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "7px", px: "14px", py: "9px",
                  backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px",
                  color: oldestAwaiting ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)",
                  fontFamily: FONT_FAMILY, fontSize: "0.84rem", fontWeight: 600, flexShrink: 0,
                  cursor: oldestAwaiting ? "pointer" : "default", textTransform: "none", transition: "all 0.15s",
                  "&:hover": oldestAwaiting ? { borderColor: "rgba(253,185,64,0.4)", color: AMBER } : {},
                }}
              >
                <SendOutlinedIcon sx={{ fontSize: "14px" }} /> Prompt QE
              </Box>
              <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.45, flex: 1, minWidth: "200px" }}>
                {oldestAwaiting
                  ? `Sends a nudge to ${qe.name} — only the QE can issue a COC. The FA can't approve or decline on their behalf.`
                  : "Nothing is awaiting a COC right now, so there's nothing to nudge about."}
              </Typography>
            </Box>
          </Box>
        ) : null}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "10px", mb: "22px" }}>
          <FAStatTile label="Installs done today" value={data.installs_done_today} sub="By the team's installers" tone="plain" />
          <FAStatTile
            label="COCs issued today"
            value={<>{data.cocs_issued_today}<Box component="span" sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}> / {data.installs_done_today}</Box></>}
            sub={qe ? `By ${qe.name}` : "No QE assigned"}
            tone="plain"
          />
          <FAStatTile
            label="Awaiting COC"
            value={data.awaiting_count}
            sub={data.oldest_waiting_at ? `Oldest waiting ${waitingSince(data.oldest_waiting_at)}` : "Nothing outstanding"}
            tone={data.awaiting_count > 0 ? "warn" : "plain"}
          />
        </Box>

        <Box sx={{ pt: "6px", pb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Awaiting COC
          </Typography>
        </Box>

        {data.awaiting.length === 0 ? (
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", py: "24px", mb: "22px" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", textAlign: "center", fontSize: "0.88rem" }}>
              Nothing awaiting a COC right now.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: "22px", display: "flex", flexDirection: "column", gap: "10px" }}>
            {data.awaiting.map((job) => (
              <Box
                key={job.reference_id}
                sx={{
                  backgroundColor: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.28)", borderRadius: "12px",
                  p: "16px 18px", display: "grid", gridTemplateColumns: { xs: "36px 1fr", sm: "40px 1fr auto" },
                  alignItems: "center", gap: "14px",
                }}
              >
                <Box sx={{ width: { xs: "36px", sm: "40px" }, height: { xs: "36px", sm: "40px" }, borderRadius: "10px", backgroundColor: "rgba(251,191,36,0.14)", color: WARN_YELLOW, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <AccessTimeIcon sx={{ fontSize: "20px" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: WARN_YELLOW, letterSpacing: "-0.01em" }}>
                    {job.address || "No address on file"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.65)", mt: "3px" }}>
                    <Box component="span" sx={{ color: "#fff", fontWeight: 600 }}>{job.installer_name || "Installer not recorded"}</Box>
                    {" · install started "}{formatTimeOfDay(job.installation_date)}
                    {" · waiting "}<Box component="span" sx={{ color: "#fff", fontWeight: 600 }}>{waitingSince(job.installation_date)}</Box>
                    {" · #"}{shortRef(job.reference_id)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", gridColumn: { xs: "1 / -1", sm: "auto" }, justifyContent: { xs: "center", sm: "flex-end" } }}>
                  {job.already_prompted ? (
                    <Box sx={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", backgroundColor: "rgba(255,255,255,0.06)", px: "9px", py: "4px", borderRadius: "6px", whiteSpace: "nowrap" }}>
                      Prompted
                    </Box>
                  ) : null}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setPromptTarget(job)}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: "6px", px: "12px", py: "8px",
                      backgroundColor: "rgba(251,191,36,0.14)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: "8px",
                      color: WARN_YELLOW, fontFamily: FONT_FAMILY, fontSize: "0.78rem", fontWeight: 600,
                      cursor: "pointer", flexShrink: 0, textTransform: "none", whiteSpace: "nowrap",
                      "&:hover": { backgroundColor: "rgba(251,191,36,0.22)" },
                    }}
                  >
                    <SendOutlinedIcon sx={{ fontSize: "13px" }} /> Prompt QE
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: "6px", pb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Today's COCs · {data.cocs_issued_today} issued
          </Typography>
          <Typography onClick={() => navigate("/QE/history/")} sx={{ fontSize: "0.7rem", color: AMBER, fontWeight: 600, cursor: "pointer" }}>
            Open QE app →
          </Typography>
        </Box>

        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
          {data.todays_cocs.length === 0 ? (
            <FAEmptyState py="24px">No COCs issued yet today.</FAEmptyState>
          ) : (
            data.todays_cocs.map((job, index) => (
              <Box
                key={job.reference_id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "50px 30px 1fr auto", sm: "60px 36px 1fr auto auto" },
                  alignItems: "center", gap: { xs: "10px", sm: "14px" }, px: { xs: "14px", sm: "16px" }, py: { xs: "12px", sm: "14px" },
                  borderBottom: index === data.todays_cocs.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                }}
              >
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.85)", fontVariantNumeric: "tabular-nums" }}>
                  {formatTimeOfDay(job.qe_actioned_at)}
                </Typography>
                <Box sx={{ width: "30px", height: "30px", borderRadius: "50%", backgroundColor: "rgba(34,197,94,0.14)", color: OK_GREEN_TEXT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <CheckIcon sx={{ fontSize: "14px" }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "#fff", letterSpacing: "-0.005em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {job.address || "No address on file"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Installed by <Box component="span" sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{job.installer_name || "not recorded"}</Box>
                    {" · issued by "}<Box component="span" sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{job.qe_name || "QE not recorded"}</Box>
                    {" · #"}{shortRef(job.reference_id)}
                  </Typography>
                </Box>
                <Box sx={{ fontSize: "0.66rem", fontWeight: 700, color: OK_GREEN_TEXT, backgroundColor: "rgba(34,197,94,0.12)", px: "9px", py: "3px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                  Issued
                </Box>
                {/* The design links through to the QE app; digital_coc_image is the
                    stored artefact when there is one (S3-only, so never in local dev). */}
                <Typography
                  component={job.digital_coc_image ? "a" : "span"}
                  href={job.digital_coc_image ? resolveMediaUrl(job.digital_coc_image) : undefined}
                  target={job.digital_coc_image ? "_blank" : undefined}
                  rel={job.digital_coc_image ? "noreferrer" : undefined}
                  onClick={job.digital_coc_image ? undefined : () => navigate("/QE/history/")}
                  sx={{ display: { xs: "none", sm: "block" }, fontSize: "0.78rem", color: AMBER, fontWeight: 600, textDecoration: "none", cursor: "pointer", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}
                >
                  View COC →
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ mt: "22px", p: "14px 16px", backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "10px", display: "flex", gap: "10px" }}>
          <InfoOutlinedIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: "14px", flexShrink: 0, mt: "2px" }} />
          <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.55 }}>
            <Box component="span" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>Read-only data</Box> · pulled from the QE app and IMS. The FA cannot approve, decline or amend a COC from this view — the only action available is to prompt the QE. All approvals remain with the qualified electrician.
          </Typography>
        </Box>
      </FAPageShell>

      {promptTarget ? (
        <PromptModal job={promptTarget} qeName={qe?.name} onClose={() => setPromptTarget(null)} />
      ) : null}
    </>
  );
}

function AttendancePill({ attendance }) {
  const meta = attendanceMeta(attendance);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.7rem", fontWeight: 600,
        px: "9px", py: "3px", borderRadius: "999px", whiteSpace: "nowrap", flexShrink: 0,
        backgroundColor: meta.bg, color: meta.color,
      }}
    >
      ● {meta.label}
    </Box>
  );
}

// StockFormShell.js:47 flags the hand-rolled fixed-inset overlay this used to be: it
// trapped no focus, closed on neither Escape nor a backdrop click, and rendered outside
